import React, { useState } from "react";
import { Check } from "lucide-react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import SecondaryButton from "../components/Buttons/SecondaryButton";
import LoadingIcons from "react-loading-icons";
import { createOrganization } from "../utils/Api";
import { useSendEmail } from "../hooks/useSendEmail";
import { organizationInvitationEmail } from "../emails/organizationInvitation";

interface CreateOrganizationModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSuccess: () => void;
}

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  isOpen,
  setIsOpen,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const { sendEmail } = useSendEmail();

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Organisation name is required");
      return;
    }
    if (!adminEmail.trim()) {
      setError("Admin email is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await createOrganization(name.trim(), adminEmail.trim());

      await sendEmail({
        to: adminEmail.trim(),
        template: organizationInvitationEmail({
          organizationName: response.data.organization_name,
          invitationUrl: response.data.invitation_url,
        }),
      });

      setSentEmail(adminEmail.trim());
      setName("");
      setAdminEmail("");
      setSuccess(true);
      onSuccess();
    } catch (err: any) {
      console.error("Failed to create organization:", err);
      setError(err.response?.data?.detail || "Failed to create organisation");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName("");
      setAdminEmail("");
      setError(null);
      setSuccess(false);
      setSentEmail("");
      setIsOpen(false);
    }
  };

  if (success) {
    return (
      <Modal isOpen={isOpen} setIsOpen={handleClose}>
        <div className="flex flex-col items-center gap-4 py-6 text-center">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <Check className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-1">
              Invitation sent
            </h3>
            <p className="text-sm text-gray-500">
              An invitation has been sent to <strong className="text-gray-700">{sentEmail}</strong>
            </p>
          </div>
          <PrimaryButton onClick={handleClose} className="w-full mt-2">
            Done
          </PrimaryButton>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} setIsOpen={handleClose}>
      <div className="w-full">
        <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-0.5">
          Create organisation
        </h3>
        <p className="text-sm text-gray-500 mb-5">
          Set up a new organisation and invite an admin.
        </p>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {error}
          </p>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="org-name" className="block text-sm font-medium text-gray-700 mb-1.5">
              Organisation name
            </label>
            <input
              type="text"
              id="org-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              placeholder="Enter organisation name"
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700 mb-1.5">
              Admin email
            </label>
            <input
              type="email"
              id="admin-email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              placeholder="admin@example.com"
              disabled={loading}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <SecondaryButton
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingIcons.TailSpin stroke="#fff" width="16" height="16" />
                  Sending...
                </span>
              ) : (
                "Send invitation"
              )}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateOrganizationModal;
